import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { MantineProvider } from '@mantine/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { Userpilot } from 'userpilot';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'services/set_environment_banner_offset.ts';

import { DndProvider } from 'react-dnd';
import { getBackendOptions, MultiBackend } from '@minoru/react-dnd-treeview';

import { APIProvider } from 'context/api/APIProvider';
import { AuthenticationProvider } from 'hooks/api/useAuthentication';
import { CONSOLE_VERSION } from 'services/constants';
import { ModalProvider } from 'context/modal/ModalProvider';
import { showFrontChat } from 'services/front_chat';

import { RootContainer } from 'views/Root/RootContainer';

import { CookieNotice } from 'components/CookieNotice/CookieNotice';

import { LUMEO_THEME } from './mantine';
import 'mantine.scss';
import 'index.scss';
import 'tailwind.css';

const queryClient = new QueryClient();
const DND_OPTIONS = getBackendOptions();

if (import.meta.env.PROD) {
  Userpilot.initialize('NX-11d5cdcc');
}

const mode = import.meta.env.MODE;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: mode === 'development' ? 'dev' : mode,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  release: CONSOLE_VERSION,
  tracesSampleRate: 1.0,
  enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
});

(window as any).lumeo = (window as any).lumeo || {};
(window as any).lumeo.showMessenger = showFrontChat;

const root = createRoot(document.getElementById('root')!);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: '*',
    element: (
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={LUMEO_THEME} defaultColorScheme="auto">
          <DndProvider backend={MultiBackend} options={DND_OPTIONS}>
            <AuthenticationProvider>
              <APIProvider>
                <TooltipProvider>
                  <ModalProvider>
                    <RootContainer />
                    <CookieNotice />
                  </ModalProvider>
                </TooltipProvider>
                <ReactQueryDevtools />
              </APIProvider>
            </AuthenticationProvider>
          </DndProvider>
        </MantineProvider>
      </QueryClientProvider>
    ),
  },
]);

root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
